// Paylaşılan React hook'ları.
// Tasarımdaki (Call Agent Admin.dc.html) tek sınıfın merkezî davranışları
// (1sn tick, roster önbelleği, Escape, döküman tıklaması) burada sayfa-yerel
// hale getirilmiştir: her sayfa yalnızca ihtiyacı olanı kurar.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getRoster } from './api';
import type { RosterAgent } from './types';

/**
 * Sayaçlar için 1sn'lik tick. `active` false iken zamanlayıcı hiç kurulmaz —
 * tasarımdaki "sadece canli-durum/molalar rotalarında tick'le" davranışı.
 */
export function useNow(intervalMs = 1000, active = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, active]);
  return now;
}

// Roster tüm oturum boyunca sabittir; tasarımdaki `this._roster` önbelleğinin
// karşılığı. Modül düzeyinde tutulur ki sayfalar arası geçiş yeniden çekmesin.
let _rosterCache: RosterAgent[] | null = null;
let _rosterInflight: Promise<RosterAgent[]> | null = null;

// Önbellek tazelendiğinde MONTE olan tüm useRoster'lara haber ver — yoksa yalnız
// tazelemeyi tetikleyen bileşen yeni listeyi görür, diğerleri eski listede kalır.
const _rosterSubs = new Set<(r: RosterAgent[]) => void>();

/** Oturum kapanınca çağrılır: modül-düzeyi önbellek bir sonraki kullanıcıya sızmasın. */
export function clearRoster(): void {
  _rosterCache = null;
  _rosterInflight = null;
}

/** Önbelleği ATLAYIP roster'ı yeniden çeker. Gerekçe: roster oturum boyu önbellekte
 *  durur, ama /api/live 30sn'de bir tazelenir — oturum ORTASINDA oluşan (ingest'in
 *  açtığı provisional) ya da yeni bir markaya eklenen ajan canlı kartta görünüp
 *  önbellekteki roster'da görünmez. Canlı karttan böyle bir ajana atlanınca çağrılır. */
export function refreshRoster(): Promise<RosterAgent[]> {
  const p = getRoster().then(
    (r) => {
      _rosterCache = r;
      _rosterInflight = p;
      _rosterSubs.forEach((fn) => fn(r));
      return r;
    },
    (err) => {
      throw err;
    },
  );
  return p;
}

export function useRoster(): RosterAgent[] {
  const [roster, setRoster] = useState<RosterAgent[]>(() => _rosterCache || []);
  useEffect(() => {
    _rosterSubs.add(setRoster);
    return () => {
      _rosterSubs.delete(setRoster);
    };
  }, []);
  useEffect(() => {
    if (_rosterCache) return;
    let alive = true;
    if (!_rosterInflight) {
      // Başarısızlıkta inflight'ı SIFIRLA — reddedilmiş promise'i sonsuza dek önbelleğe
      // alıp her roster seçicisini kalıcı boş bırakma (bir geçici hata tüm oturumu
      // sakatlıyordu). Sonraki mount yeniden dener.
      _rosterInflight = getRoster().then(
        (r) => { _rosterCache = r; return r; },
        (err) => { _rosterInflight = null; throw err; },
      );
    }
    _rosterInflight.then((r) => { if (alive) setRoster(r); }).catch(() => { /* boş kalır, sonra yeniden denenir */ });
    return () => {
      alive = false;
    };
  }, []);
  return roster;
}

/** Escape tuşu — modal/açılır menü kapatma. */
export function useEscape(active: boolean, onEscape: () => void): void {
  const ref = useRef(onEscape);
  ref.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') ref.current();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [active]);
}

/** Döküman tıklaması — dışarı tıklayınca açılır menüyü kapat. */
export function useDocumentClick(active: boolean, onClick: () => void): void {
  const ref = useRef(onClick);
  ref.current = onClick;
  useEffect(() => {
    if (!active) return;
    const h = () => ref.current();
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [active]);
}

/**
 * Genişletilebilir satırlar için açık/kapalı kümesi.
 * (Raporlar, Molalar, Uyarılar, Ayarlar/Temsilciler aynı deseni kullanır.)
 */
export function useExpanded(): {
  expanded: Record<string, boolean>;
  toggle: (id: string) => void;
  isOpen: (id: string) => boolean;
} {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = useCallback((id: string) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  }, []);
  const isOpen = useCallback((id: string) => !!expanded[id], [expanded]);
  return { expanded, toggle, isOpen };
}
